import React, {useEffect, useRef, useState} from "react";
import {
    checkboxChecked,
    clearErrors,
    emailValid,
    markError,
    required,
    validateEmail
} from "../contexts/actions/form/validate";
import FormContactUs from "./FormContactUs";

window.privacyUrl = window.privacyUrl || '';

const CareerForm = (props) => {
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [filesOnUpload, setFilesOnUpload] = useState([]);
    const allowedExtensions = /(\.doc|\.docx|\.odt|\.pdf|\.rtf)$/i;
    const [fileName, setFileName] = useState('Lebenslauf hochladen (.pdf, .doc, .docx)');

    const contactForm = useRef(null);
    const name = useRef(null);
    const files = useRef(null);
    const fileLabel = useRef(null);
    const phone = useRef(null);
    const email = useRef(null);
    const message = useRef(null);
    const gdpr = useRef(null);
    const modalMessageForm = useRef(null);

    useEffect(() => {
        if (filesOnUpload.length > 0) {
            if (!validFile()) {
                files.current.parentElement.classList.add('error');
                setFilesOnUpload([]);
                alert('Der Lebenslauf muss .pdf, .doc oder .docx sein!')
            } else {
                files.current.parentElement.classList.remove('error');
            }
        }
    }, [filesOnUpload])

    useEffect(() => {
        const label = fileLabel.current;
        if (!label) return;

        label.addEventListener('dragenter', handleDragIn);
        label.addEventListener('dragleave', handleDragOut);
        label.addEventListener('dragover', handleDrag);
        label.addEventListener('drop', handleDrop);

        return () => {
            label.removeEventListener('dragenter', handleDragIn);
            label.removeEventListener('dragleave', handleDragOut);
            label.removeEventListener('dragover', handleDrag);
            label.removeEventListener('drop', handleDrop);
        };
    }, []);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }
    const handleDragIn = (e) => {
        e.preventDefault();
        e.stopPropagation();
        files.current.parentElement.classList.add('drag');
    }
    const handleDragOut = (e) => {
        e.preventDefault();
        e.stopPropagation();
        files.current.parentElement.classList.remove('drag');
    }

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        files.current.parentElement.classList.remove('drag');

        const dropped = e.dataTransfer?.files;
        if (dropped && dropped.length > 0) {
            const dt = new DataTransfer();
            Array.from(dropped).forEach(f => dt.items.add(f));
            files.current.files = dt.files;

            setFilesOnUpload([...dt.files]);
            validFile();
        }
    };

    const handleChangeFileInput = () => {
        setFilesOnUpload([...files.current.files]);
        validFile();
    };

    const validFile = () => {
        const el = files.current;
        const setFile = el?.files?.[0];
        if (!setFile || !allowedExtensions.test(setFile.name)) {
            if (el) el.value = "";
            el?.parentElement?.classList.add('error');
            setFileName('Lebenslauf hochladen (.pdf, .doc, .docx)');
            return false;
        }
        setFileName(setFile.name);
        el?.parentElement?.classList.remove('error');
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (window.grecaptcha === undefined) {
            window.location.reload();
        }

        clearErrors([name, email, message, phone, files, gdpr], {reflow: true});

        let valid = true;

        const firstErrorRefs = [];

        if(!required(name)) { markError(name); valid = false; firstErrorRefs.push(name); }
        if(!emailValid(email, validateEmail)) { markError(email); valid = false; firstErrorRefs.push(email); }
        if (!required(message))    { markError(message); valid = false; firstErrorRefs.push(message); }
        if (!required(phone)) { markError(phone); valid = false; firstErrorRefs.push(phone); }
        if (!checkboxChecked(gdpr)) { markError(gdpr); valid = false; firstErrorRefs.push(gdpr); }
        if (!validFile()) { markError(files); valid = false; firstErrorRefs.push(files); }

        if (!valid) {
            const first = firstErrorRefs[0];
            if (first) markError(first, { focus: true, scroll: true });
            return;
        }

        if (valid) {
            const csrfToken = document.head.querySelector("[name~=csrf-token][content]").content;
            setSending(true);

            const formData = new FormData(contactForm.current);
            formData.append('career_id', props.careerId);

            window.grecaptcha.ready(async function () {
                const token = await window.grecaptcha.execute(window.captchaSiteKey || '6LdPEQMpAAAAAFll-gXz9LEZSAvZ558tn4nWQ8c-', {action: 'submit'});
                formData.append("g-recaptcha-response", token);

                const response = await fetch('/api/career-form', {
                    headers: {
                        "Accept": "application/json",
                        "X-CSRF-Token": csrfToken
                    },
                    'method': 'POST',
                    body: formData
                });
                const data = await response.json();
                if (data) {
                    if (response.status === 200) {
                        contactForm.current.reset();

                        setFilesOnUpload([]);
                        setFileName('Lebenslauf hochladen (.pdf, .doc, .docx)');

                        setSent(true);
                        setSending(false);
                        setFilesOnUpload([]);
                    } else if (response.status === 500) {
                        alert(response.statusText);
                    } else {
                        let errorMessage = '';
                        for (let error in data.errors) {
                            errorMessage = data.errors[error][0];
                            break;
                        }
                        setSent(true);
                        setSending(false);
                        alert(errorMessage);
                    }
                }
            });
        }
    }

    const truncate = (source, size) => {
        return source.length > size ? source.slice(0, size - 1) + "…" : source;
    }

    return (
        <div className="bg-black-full px-4 pt-12 pb-16 text-white lg:!p-16">
            <div className="flex flex-col lg:flex-row lg:gap-12 mx-auto">

                <div className="w-full lg:w-1/2 p-0 md:pr-32">
                    <div className="flex flex-col gap-6">
                        <h2 className="font-28 mb-4">Interessieren Sie sich für eine Stelle?</h2>
                        <p className="paragraph-2 text-neutrals-400 font-light">
                            Füllen Sie das Bewerbungsformular aus und unser Profirol-Experte meldet sich in Kürze bei Ihnen, berät Sie und geht die wichtigsten technischen Parameter mit Ihnen durch. Wir benötigen jetzt nur die wichtigsten Informationen – es dauert nur eine Minute.
                        </p>
                    </div>

                    <FormContactUs />
                </div>

                <div className="w-full lg:w-1/2 space-y-6">
                    <form className="w-full space-y-6 text-white product-form-placeholder"
                          method="post"
                          onSubmit={handleSubmit}
                          ref={contactForm}
                          encType="multipart/form-data">

                        <div className="relative floating-label">
                            <input
                                ref={name}
                                className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                                name="name"
                                type="text"
                                placeholder=" "
                            />
                            <label className="text-neutrals-400">Vor- und Nachname*</label>
                        </div>

                        <div className="relative floating-label">
                            <input
                                ref={email}
                                className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                                name="email"
                                type="text"
                                placeholder=" "
                            />
                            <label className="text-neutrals-400">E-Mail*</label>
                        </div>

                        <div className="relative floating-label">
                            <input
                                ref={phone}
                                name="phone"
                                className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                                type="text"
                                placeholder=" "
                            />
                            <label className="text-neutrals-400">Telefon*</label>
                        </div>

                        <div className="relative floating-label">
                            <textarea
                                name="message"
                                ref={message}
                                rows={5}
                                className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                                placeholder=" "
                            />
                            <label className="text-neutrals-400">Nachricht*</label>
                        </div>

                        <div className="">
                            <label
                                ref={fileLabel}
                                htmlFor="file"
                                className={
                                    "!relative w-full px-4 py-4 border-2 border-dashed border-neutrals-700 rounded flex items-center gap-3 cursor-pointer transition-colors " +
                                    ((filesOnUpload[0] && filesOnUpload[0].name) ? "text-white" : "text-neutrals-400")
                                }
                            >
                                <img src="/images/icons/other/icon_upload.svg" alt="" className="w-5 h-5 shrink-0" />
                                <span className="truncate">
                                    {truncate((filesOnUpload[0]?.name ?? fileName), 32)}
                                </span>
                            </label>

                            <input
                                id="file"
                                ref={files}
                                name="file"
                                type="file"
                                className="hidden"
                                onChange={handleChangeFileInput}
                                placeholder="Lebenslauf hier hochladen"
                            />
                        </div>

                        <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
                            <div className="checkbox-container">
                                <label
                                    htmlFor="gdpr"
                                    className="flex items-start gap-3 cursor-pointer"
                                >
                                    <span className="custom-checkbox">
                                        <input id="gdpr" ref={gdpr} type="checkbox" />
                                        <span className="checkmark"></span>
                                    </span>
                                    <p className="text-12 text-neutrals-400 leading-snug letter-spacing-2per line-height-130">
                                        Mit dem Absenden der Anfrage stimme ich der{" "}
                                        <a target="_blank" href={window.privacyUrl} className="underline">
                                            Verarbeitung personenbezogener Daten
                                        </a>{" "}
                                        durch Profirol s.r.o. gemäß den Datenschutzbestimmungen zu.
                                    </p>
                                </label>
                            </div>

                            <button
                                type="submit"
                                className="form-btn self-start w-full lg:w-auto hover:bg-neutrals-500 transition duration-200 hover:text-white disabled:opacity-60"
                                disabled={sending}
                            >
                                {sending ? "Wird gesendet..." : "Absenden"}
                            </button>
                        </div>
                    </form>

                    {sent &&
                        <section className="form-message animated fadeIn go" ref={modalMessageForm}>
                            <div className="form-message__content">
                                <h3>
                                    Vielen Dank für Ihr Interesse, bei uns zu arbeiten
                                </h3>
                                <p className="text-center">
                                    Wir schätzen Ihr Interesse sehr. In den nächsten Tagen prüfen wir alle Lebensläufe und
                                    kontaktieren ausgewählte Bewerber.
                                </p>
                                <button
                                    onClick={() => {
                                        setSent(false);
                                    }}
                                    className="btn btn--primary"
                                >
                                    Schließen
                                </button>
                            </div>
                        </section>
                    }
                </div>
            </div>
        </div>
    );
}

export default CareerForm;
